import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LogOut, LayoutDashboard, Building2, Users, CreditCard,
  Database, Handshake, Gift, Globe, LifeBuoy, ScrollText, Tag, Plane, Sparkles,
  DollarSign, Mail, ChevronDown, Check, MonitorPlay, BarChart3, Newspaper,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AdminOverview from "./admin/AdminOverview";
import AdminOrganizations from "./admin/AdminOrganizations";
import AdminUsers from "./admin/AdminUsers";
import AdminSubscriptions from "./admin/AdminSubscriptions";
import AdminDataSources from "./admin/AdminDataSources";
import AdminPartnerships from "./admin/AdminPartnerships";
import AdminReferrals from "./admin/AdminReferrals";
import AdminGsa from "./admin/AdminGsa";
import AdminSupport from "./admin/AdminSupport";
import AdminAudit from "./admin/AdminAudit";
import AdminPricing from "./admin/AdminPricing";
import AdminMarketData from "./admin/AdminMarketData";
import AdminTourismData from "./admin/AdminTourismData";
import AdminAiObservability from "./admin/AdminAiObservability";
import AdminAiPricing from "./admin/AdminAiPricing";
import AdminProspects from "./admin/AdminProspects";
import AdminBspData from "./admin/AdminBspData";
import AdminNewsSignals from "./admin/AdminNewsSignals";

const NAV = [
  { key: "overview", label: "Vue d'ensemble", icon: LayoutDashboard, C: AdminOverview },
  { key: "organizations", label: "Organizations", icon: Building2, C: AdminOrganizations },
  { key: "users", label: "Users", icon: Users, C: AdminUsers },
  { key: "prospects", label: "Prospects", icon: Mail, C: AdminProspects },
  { key: "subscriptions", label: "Subscriptions", icon: CreditCard, C: AdminSubscriptions },
  { key: "pricing", label: "Pricing & Plans", icon: Tag, C: AdminPricing },
  { key: "market-data", label: "Lodge Market Data", icon: Plane, C: AdminMarketData },
  { key: "bsp-data", label: "Lodge BSP Data", icon: BarChart3, C: AdminBspData },
  { key: "tourism-data", label: "Lodge Tourism Data", icon: Database, C: AdminTourismData },
  { key: "news-signals", label: "News / Market Signals", icon: Newspaper, C: AdminNewsSignals },
  { key: "ai-observability", label: "AI Observability", icon: Sparkles, C: AdminAiObservability },
  { key: "ai-pricing", label: "AI Pricing", icon: DollarSign, C: AdminAiPricing },
  { key: "data-sources", label: "Data Sources", icon: Database, C: AdminDataSources },
  { key: "partnerships", label: "Partner Relationships", icon: Handshake, C: AdminPartnerships },
  { key: "referrals", label: "Referrals", icon: Gift, C: AdminReferrals },
  { key: "gsa", label: "GSA Activation", icon: Globe, C: AdminGsa },
  { key: "support", label: "Support Status", icon: LifeBuoy, C: AdminSupport },
  { key: "audit", label: "Audit Log", icon: ScrollText, C: AdminAudit },
];

export default function AdminPanel() {
  const { user, memberships, setActiveOrgId, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(() => new URLSearchParams(window.location.search).get("tab") === "users" ? "users" : "overview");

  if (user === undefined) return null;
  if (!user || !user.is_platform_admin) return <Navigate to="/login" replace />;

  const Active = NAV.find((n) => n.key === active)?.C || AdminOverview;
  const demoMemberships = (memberships || []).filter((m) => m.org?.is_demo === true);

  const openWorkspace = (membership) => {
    setActiveOrgId(membership.org.id);
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF6]" data-testid="admin-panel">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <div>
            <p className="font-heading text-sm font-bold text-[#17201A]">Lodge & Co — Administration</p>
            <p className="flex items-center gap-1 text-xs text-[#3E7256]"><ShieldCheck size={12} /> {user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="admin-space-switcher"
                className="inline-flex items-center gap-2 rounded-lg border border-[#B7D5C2] bg-[#F1F7F3] px-3 py-2 text-sm font-semibold text-[#1E3D2B] transition-colors hover:bg-[#E3F1E8]"
              >
                <MonitorPlay size={16} /> Changer d'espace <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                Espace actif
              </DropdownMenuLabel>
              <DropdownMenuItem disabled className="flex items-center justify-between py-2 font-semibold text-[#1E3D2B] opacity-100">
                <span className="flex items-center gap-2"><ShieldCheck size={15} /> Administration plateforme</span>
                <Check size={15} className="text-[#3E7256]" />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {demoMemberships.length > 0 ? demoMemberships.map((membership) => (
                <DropdownMenuItem
                  key={membership.org.id}
                  data-testid={`admin-space-option-${membership.org.id}`}
                  onClick={() => openWorkspace(membership)}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-[#17201A]">{membership.org.name}</span>
                    <span className="text-[11px] text-[#B45309]">Démonstration</span>
                  </span>
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">DÉMO</span>
                </DropdownMenuItem>
              )) : (
                <DropdownMenuItem disabled data-testid="admin-space-no-demo" className="py-2 text-slate-400">
                  Aucune organisation Démo accessible
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="admin-space-manage-organizations"
                onClick={() => setActive("organizations")}
                className="py-2 text-slate-600"
              >
                <Building2 size={15} /> Gérer les organisations
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-6 py-6">
        <nav className="sticky top-6 h-fit w-56 shrink-0 space-y-1" data-testid="admin-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const on = active === n.key;
            return (
              <button key={n.key} data-testid={`admin-tab-${n.key}`} onClick={() => setActive(n.key)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${on ? "bg-[#1E3D2B] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon size={16} /> {n.label}
              </button>
            );
          })}
        </nav>
        <main className="min-w-0 flex-1">
          <Active />
        </main>
      </div>
    </div>
  );
}
